import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Download, ExternalLink, FileText, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { bookActivitiesService } from '@/services/book-activities.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { BookActivityView } from '@/types/api';

/**
 * BookActivitiesManager — admin CRUD card for the per-book downloadable
 * activity PDFs that appear on the reader's "Book Resources" tab.
 *
 * Modelled on `BookPromptManager`:
 *   - On mount + after every mutation we re-fetch the list rather than
 *     splicing locally, so any server-side ordering changes (or future
 *     moderation state) stay authoritative.
 *   - Inline rename per row via a Pencil → text input toggle.
 *   - Delete confirmation through `ConfirmDialog` (PDFs are content we
 *     don't want to lose by mis-click).
 *   - "Add activity" form at the bottom takes a title + file picker.
 *     The file part is PDF-only (server enforces the mime allowlist;
 *     we mirror the constraint client-side for a faster error path).
 *
 * The PDF itself is immutable on the row — to replace bytes the admin
 * deletes the activity and creates a new one. Matches the
 * Replace/Remove pattern on the cover uploader without the extra
 * complexity of a "swap the file but keep the row" code path.
 */
export interface BookActivitiesManagerProps {
  bookId: string;
  className?: string;
}

const ACCEPTED_TYPES = ['application/pdf'];
// 50 MB — matches ACTIVITY_MAX_BYTES in the server's book-activities storage
// service. Activities opt out of the tighter 10 MB global multipart cap that
// still applies to covers and thumbnails; keep the two numbers in sync.
const MAX_SIZE_MB = 50;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export function BookActivitiesManager({ bookId, className }: BookActivitiesManagerProps) {
  const [activities, setActivities] = useState<BookActivityView[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Inline rename state. Only one row is editable at a time.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);

  // "Add activity" form state.
  const [newTitle, setNewTitle] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [adding, setAdding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation.
  const [deleting, setDeleting] = useState<BookActivityView | null>(null);
  const [deletingPending, setDeletingPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await bookActivitiesService.list(bookId);
        if (cancelled) return;
        setActivities(rows);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load activities.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  const refetch = async () => {
    try {
      const rows = await bookActivitiesService.list(bookId);
      setActivities(rows);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not refresh activities.');
    }
  };

  const handleStartEdit = (activity: BookActivityView) => {
    setEditingId(activity.id);
    setEditingDraft(activity.title);
  };

  const handleSaveEdit = async (activity: BookActivityView) => {
    const trimmed = editingDraft.trim();
    if (trimmed.length === 0) {
      toast.error('Title cannot be empty.');
      return;
    }
    if (trimmed === activity.title) {
      // No-op edit — just close the input.
      setEditingId(null);
      setEditingDraft('');
      return;
    }
    setSavingId(activity.id);
    try {
      await bookActivitiesService.updateTitle(activity.id, trimmed);
      setEditingId(null);
      setEditingDraft('');
      await refetch();
      toast.success('Activity renamed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the activity.');
    } finally {
      setSavingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDraft('');
  };

  const handleAdd = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = newTitle.trim();
    if (trimmed.length === 0) {
      toast.error('Give the activity a title before uploading.');
      return;
    }
    if (!newFile) {
      toast.error('Pick a PDF to upload.');
      return;
    }
    if (!ACCEPTED_TYPES.includes(newFile.type) && !newFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Activities must be PDF files.');
      return;
    }
    if (newFile.size > MAX_SIZE_BYTES) {
      toast.error(`Activity PDFs must be ${MAX_SIZE_MB}MB or smaller.`);
      return;
    }
    setAdding(true);
    try {
      await bookActivitiesService.create(bookId, trimmed, newFile);
      setNewTitle('');
      setNewFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await refetch();
      toast.success('Activity added.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not add the activity.');
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleting) return;
    setDeletingPending(true);
    try {
      await bookActivitiesService.remove(deleting.id);
      setDeleting(null);
      await refetch();
      toast.success('Activity removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete the activity.');
    } finally {
      setDeletingPending(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-2xl border border-or-slate-200 bg-white p-5 shadow-sm',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="or-kicker text-or-slate-500">Activities</div>
          <h2 className="mt-1 font-display text-[16px] font-bold text-or-slate-900">
            Downloadable PDFs
          </h2>
          <p className="mt-1 text-[12px] leading-relaxed text-or-slate-500">
            Worksheets, coloring pages, discussion guides. Readers see these on the book’s
            Resources tab.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {loadError ? (
          <div className="rounded-lg border border-or-rose-200 bg-or-rose-50 px-3 py-2 text-[12px] text-or-rose-700">
            {loadError}
          </div>
        ) : null}

        {activities === null && !loadError ? (
          <div className="text-[12px] text-or-slate-500">Loading…</div>
        ) : null}

        {activities && activities.length === 0 ? (
          <div className="rounded-lg border border-dashed border-or-slate-200 bg-or-cream-50 px-3 py-4 text-center text-[12px] text-or-slate-500">
            No activities yet. Add the first one below.
          </div>
        ) : null}

        {activities && activities.length > 0 ? (
          <ul className="flex flex-col divide-y divide-or-slate-100">
            {activities.map((activity) => {
              const isEditing = editingId === activity.id;
              const isSaving = savingId === activity.id;
              return (
                <li key={activity.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <span
                      aria-hidden
                      className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-or-orange-100 text-or-orange-700"
                    >
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      {isEditing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            type="text"
                            value={editingDraft}
                            onChange={(e) => setEditingDraft(e.target.value)}
                            disabled={isSaving}
                            maxLength={300}
                            className="h-9 min-w-[200px] flex-1 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
                            autoFocus
                          />
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => void handleSaveEdit(activity)}
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleCancelEdit}
                            disabled={isSaving}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <span className="break-words text-[13px] font-semibold text-or-slate-900">
                            {activity.title}
                          </span>
                          <span className="text-[11px] text-or-slate-500">
                            {formatRelative(activity.createdAt)}
                          </span>
                        </>
                      )}
                    </div>
                    {!isEditing ? (
                      <div className="flex shrink-0 items-center gap-1">
                        <a
                          href={activity.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-slate-500 hover:bg-or-slate-100 hover:text-or-slate-800"
                          title="Open PDF in a new tab"
                          aria-label="Open PDF in a new tab"
                        >
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                        </a>
                        <a
                          href={activity.fileUrl}
                          download
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-slate-500 hover:bg-or-slate-100 hover:text-or-slate-800"
                          title="Download PDF"
                          aria-label="Download PDF"
                        >
                          <Download className="h-3.5 w-3.5" aria-hidden />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleStartEdit(activity)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-slate-500 hover:bg-or-slate-100 hover:text-or-slate-800"
                          title="Rename"
                          aria-label="Rename activity"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(activity)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-slate-500 hover:bg-or-rose-100 hover:text-or-rose-700"
                          title="Delete"
                          aria-label="Delete activity"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} className="mt-4 flex flex-col gap-2 border-t border-or-slate-100 pt-4">
        <label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
          Add an activity
        </label>
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Activity title, e.g. “Chapter 1 vocabulary worksheet”"
          maxLength={300}
          disabled={adding}
          className="h-9 w-full rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-3 py-1 text-[12px] font-semibold text-or-slate-700 hover:bg-or-slate-50">
            <Upload className="h-3.5 w-3.5" aria-hidden />
            {newFile ? 'Change PDF' : 'Choose PDF'}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setNewFile(file);
              }}
            />
          </label>
          {newFile ? (
            <span className="truncate text-[12px] text-or-slate-600" title={newFile.name}>
              {newFile.name}
            </span>
          ) : (
            <span className="text-[12px] text-or-slate-400">PDF · max {MAX_SIZE_MB}MB</span>
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={adding || !newTitle.trim() || !newFile}
            className="ml-auto"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            {adding ? 'Uploading…' : 'Add activity'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
        title="Delete this activity?"
        description={
          deleting
            ? `Delete "${deleting.title}"? Readers will no longer be able to download this PDF. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        isConfirming={deletingPending}
        onConfirm={() => void handleConfirmDelete()}
      />
    </div>
  );
}

/**
 * Lightweight relative-time formatter — keeps the row metadata terse
 * without dragging in a date library. Falls through to an ISO date for
 * anything older than a couple of weeks.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'Just now';
  if (diff < hour) return `Added ${Math.floor(diff / minute)} min ago`;
  if (diff < day) return `Added ${Math.floor(diff / hour)} h ago`;
  if (diff < 14 * day) return `Added ${Math.floor(diff / day)} d ago`;
  return `Added ${new Date(iso).toLocaleDateString()}`;
}
